import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { sendableSteps } from '@atlaskit/prosemirror-collab';
import { type Transaction } from '@atlaskit/editor-prosemirror/state';

import { usePageContentId } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import { StepsDatabase } from './stepsDatabase';
import { useBroadcastChannel } from './broadcastChannel';
import {
	createPlugin,
	editorOfflineStoragePluginKey,
	type EditorOfflineStorageDisabledReason,
} from './pm-plugins/main';
import { restoreStepsAction } from './pm-plugins/actions';
import { syncWithOfflineStorage } from './pm-plugins/syncWithOfflineStorage';
import { useResyncOnline } from './ui/hooks/useResyncOnline';
import { useSyncEnabled } from './ui/hooks/useSyncEnabled';
import { useCreateOfflineVersion } from './ui/hooks/useCreateOfflineVersion';
import { showVersionInsertionErrorFlag } from './ui/versionInsertionErrorFlag';

export const editorOfflineStoragePlugin: EditorOfflineStoragePlugin = ({ api, config }) => {
	const stepsDatabase = new StepsDatabase();

	return {
		name: 'editorOfflineStorage',
		pmPlugins() {
			return [
				{
					name: 'editorOfflineStoragePlugin',
					plugin: createPlugin(stepsDatabase),
				},
			];
		},
		getSharedState(state) {
			if (!state) {
				return undefined;
			}
			const { status, disabled, inactiveSyncing } =
				editorOfflineStoragePluginKey.getState(state) ?? {};
			const { version, steps } = sendableSteps(state) ?? {};

			return {
				version: version ?? 0,
				steps: steps ?? [],
				status: status ?? 'pending',
				disabled: disabled ?? false,
				inactiveSyncing: inactiveSyncing ?? false,
			};
		},
		usePluginHook: ({ editorView }) => {
			const [contentId] = usePageContentId();
			const disabledRef = useRef<EditorOfflineStorageDisabledReason | boolean>(false);
			const hasCheckedForVersionInsertionBackupRef = useRef(false);
			const intl = useIntl();

			usePluginStateEffect(api, ['editorOfflineStorage'], ({ editorOfflineStorageState }) => {
				disabledRef.current = editorOfflineStorageState?.disabled ?? false;
			});

			const { createVersion: createOfflineVersion, manualRetryCreateVersion } =
				useCreateOfflineVersion({
					contentId,
					api,
					isLivePage: config?.isLivePage ?? false,
					stepsDatabase,
					flags: config?.flags ?? null,
				});

			useEffect(() => {
				if (contentId && !stepsDatabase.dbName && !disabledRef.current) {
					void stepsDatabase.init(contentId, api);
				}
				// We need stepsDatabase in deps to detect when DB is no longer initialised on re-render, e.g. when page goes full-width.
			}, [contentId, stepsDatabase, api]); // eslint-disable-line react-hooks/exhaustive-deps

			useEffect(() => {
				if (fg('platform_editor_offline_editing_web_ga_fixes')) {
					// Check for existing version insertion backup after database is initialised (only once per mount)
					if (!hasCheckedForVersionInsertionBackupRef.current && stepsDatabase.dbName) {
						hasCheckedForVersionInsertionBackupRef.current = true;
						void stepsDatabase.getVersionInsertionBackup().then((versionInsertionBackup) => {
							if (versionInsertionBackup) {
								showVersionInsertionErrorFlag(
									config?.flags,
									intl,
									// onRetry: Call manual retry with stored content
									() => {
										void manualRetryCreateVersion(versionInsertionBackup.adfContent);
									},
									// onDismiss: Clear the version insertion backup from IndexedDB
									() => {
										void stepsDatabase.clearVersionInsertionBackup();
									},
								);
							}
						});
					}
				}
				// stepsDatabase.dbName is intentionally in deps to detect DB reinitialization (e.g., page goes full-width)
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [stepsDatabase.dbName, intl, manualRetryCreateVersion]);

			const broadcastUpdate = useBroadcastChannel({ api, editorView, stepsDatabase });

			/**
			 * This is to resync the document online once we move from offline -> online
			 */
			useResyncOnline({
				api,
				editorView,
				broadcastUpdate,
				stepsDatabase,
				contentId,
				isLivePage: config?.isLivePage ?? false,
				flags: config?.flags ?? null,
			});

			/**
			 * See if it's safe to re-enable the plugin if it's been disabled due to the lock being held
			 */
			useSyncEnabled({
				api,
				stepsDatabase,
				disabledRef,
			});

			// Clean up the sync timeout on unmount
			// Otherwise its possible that it will fire with stale data after unmount
			useEffect(() => {
				return () => {
					syncWithOfflineStorage.cancel();
				};
			}, [contentId, editorView]);

			/**
			 * This is to handle the initial load of the editor and syncing it from the indexeddb
			 */
			usePluginStateEffect(
				api,
				['editorOfflineStorage', 'connectivity', 'collabEdit'],
				({ editorOfflineStorageState, connectivityState, collabEditState }) => {
					const { status, steps, version, disabled } = editorOfflineStorageState ?? {};

					if (disabledRef.current) {
						return;
					}

					if (window?.indexedDB === undefined) {
						// If indexeddb is not available disable offline editing here
						if (!disabled) {
							api?.core.actions.execute(({ tr }: { tr: Transaction }) =>
								tr.setMeta(editorOfflineStoragePluginKey, {
									disabled: 'stepsDatabaseError',
								}),
							);
						}
						return;
					}

					// Both the restore (init) sync lock and the resync online (online <-> offline) sync lock are released here.
					// We confirm the steps have synced with NCS before clearing the database and releasing the lock.
					if (steps?.length === 0 && stepsDatabase.hasLock) {
						void stepsDatabase.clear(false).then(async (cleared) => {
							if (cleared) {
								if (stepsDatabase.lockPurpose === 'restore') {
									// After we've the steps confirmed with NCS, we update the doc and version in the database.
									await stepsDatabase.updateDocAndVersion(editorView).then((isUpdated) => {
										if (isUpdated) {
											// Notify other tabs to sync from the updated doc/version in IndexedDB.
											broadcastUpdate('restore');
										}
									});
								}
								await stepsDatabase.releaseSyncLock?.();
							}
						});
					}

					switch (status) {
						case 'pending':
							// Restore steps once collab edit plugin is initialised
							if (collabEditState?.initialised.collabInitialisedAt) {
								const tr: Transaction = editorView.state.tr;
								tr.setMeta('addToHistory', false);
								tr.setMeta(editorOfflineStoragePluginKey, {
									status: 'restoring',
								});
								editorView.dispatch(tr);
								void restoreStepsAction(api, stepsDatabase, editorView, 0, {
									createOfflineVersion,
								})(editorView.state.schema);
							}
							break;

						case 'restoring':
							// Do nothing until the steps are restored
							break;

						case 'ready':
							const isFocusedAndVisible =
								document.visibilityState === 'visible' && document.hasFocus();
							// If we are offline and we have steps to store, update the steps in the database
							if (
								isFocusedAndVisible &&
								steps &&
								version !== undefined &&
								connectivityState?.mode === 'offline'
							) {
								void syncWithOfflineStorage({
									editorView,
									stepsDatabase,
									version,
									steps,
									broadcast: broadcastUpdate,
								});
							}
							break;

						case 'tooManyRetries':
							// Unable to restore steps - ensure we disable the offline editing session to be safe
							// in case there's something wrong with the DB.
							if (!disabled) {
								api?.core.actions.execute(({ tr }: { tr: Transaction }) =>
									tr.setMeta(editorOfflineStoragePluginKey, {
										disabled: 'tooManyRetries',
									}),
								);
							}
							break;

						default:
							break;
					}
				},
			);
		},
	};
};
